import type { TestProjectJobDto, TestProjectsDto, TestProjectsResetDto } from '@nsb/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorText, useAction, type ApiError } from '../../api.ts';
import { TEST_PROJECT_WORDS } from '../../components/testProjectWords.ts';
import { Button, Confirm, ErrorLine, Section, Tag, TextLink } from '../../components/ui.tsx';
import { ADDED_BY_ITSELF, differencesWords, failedWords, jobWord, resetLine, resetPrompt } from './testProjectWords.ts';

/**
 * Settings → Development tools → Test projects (Jason, 2026-10-08: "we leave the test projects in development so i can
 * play with them during development, with an option to reset the test projects under development tools"): how many
 * there are, and Reset the test projects, asked once inline, which deletes every one on this machine for good and makes
 * the documented jobs again through the process, each from its recipe (API domain/test-projects.ts). Then where it
 * stands, read again whenever the page comes back into view and on Look again, never on a timer. Shown only where the
 * API says it is available: development against this machine's own database, an admin, from this machine.
 */
export function TestProjectsSection() {
  const qc = useQueryClient();
  const { data, error, refetch, isFetching } = useQuery<TestProjectsDto, ApiError>({
    queryKey: ['test-projects'],
    queryFn: () => api<TestProjectsDto>('/dev/test-projects'),
    // Read again each time the page comes back into view: a reset runs for minutes.
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });
  const [asking, setAsking] = useState(false);
  const start = useAction(() => api<TestProjectsDto>('/dev/test-projects/reset', { method: 'POST', body: {} }), {
    onSuccess: (d) => {
      qc.setQueryData(['test-projects'], d);
      setAsking(false);
    },
  });
  if (error && !data) {
    return (
      <Section title="Test projects">
        <p className="text-alert">{error.message}</p>
      </Section>
    );
  }
  if (!data?.available) return null;
  return (
    <TestProjectsView
      data={data}
      asking={asking}
      busy={start.isPending}
      error={errorText(start.error)}
      looking={isFetching}
      onAsk={() => {
        start.reset();
        setAsking(true);
      }}
      onReset={() => start.mutate(undefined)}
      onCancel={() => setAsking(false)}
      onLook={() => void refetch()}
    />
  );
}

/** The section as it reads from what the API says (held by apps/web/test/test-projects-section.test.tsx). */
export function TestProjectsView({
  data,
  asking,
  busy,
  error,
  looking,
  onAsk,
  onReset,
  onCancel,
  onLook,
}: {
  data: TestProjectsDto;
  asking: boolean;
  busy: boolean;
  error: string | null;
  looking: boolean;
  onAsk: () => void;
  onReset: () => void;
  onCancel: () => void;
  onLook: () => void;
}) {
  const n = data.testProjects;
  const jobs = data.recipes.length;
  const running = data.reset?.running ?? false;
  return (
    <Section title="Test projects">
      <p className="max-w-measure text-ink-2">
        {n === 0 ? (
          'No test projects on this machine yet. '
        ) : (
          <>
            <span className="num">{n}</span> {n === 1 ? 'test project' : 'test projects'} on this machine, {TEST_PROJECT_WORDS.kept}.{' '}
          </>
        )}
        Resetting deletes every test project here for good, then makes the <span className="num">{jobs}</span> documented jobs again, each built through the process from its
        recipe. It takes a few minutes, and nothing is painted or summed up for them. {ADDED_BY_ITSELF}
      </p>
      {running ? null : asking ? (
        <Confirm prompt={resetPrompt(n, jobs)} yes="Reset them" no="Cancel" busy={busy} onYes={onReset} onNo={onCancel} />
      ) : (
        <div className="mt-4">
          <Button tone="secondary" onClick={onAsk}>
            Reset the test projects
          </Button>
        </div>
      )}
      <ErrorLine error={error} />
      {data.reset ? <ResetState r={data.reset} looking={looking} onLook={onLook} /> : null}
    </Section>
  );
}

const TONES: Record<TestProjectJobDto['state'], 'plain' | 'moss' | 'alert' | 'cedar'> = {
  waiting: 'plain',
  playing: 'cedar',
  made: 'moss',
  failed: 'alert',
};

function ResetState({ r, looking, onLook }: { r: TestProjectsResetDto; looking: boolean; onLook: () => void }) {
  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6">
        <p className="max-w-measure">{resetLine(r)}</p>
        <Button tone="quiet" busy={looking} onClick={onLook}>
          Look again
        </Button>
      </div>
      {r.error ? <p className="mt-1 max-w-measure text-sm text-alert">It stopped before it was through: {r.error}</p> : null}
      {r.jobs.length ? (
        <ul className="mt-2 divide-y divide-rule border-y border-rule">
          {r.jobs.map((j) => (
            <li key={j.label} className="py-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="num w-10 shrink-0 text-ink-2">{j.label}</span>
                <span className="min-w-0 flex-1 basis-56">{j.projectId ? <TextLink to={`/p/${j.projectId}`}>{j.name}</TextLink> : j.name}</span>
                <Tag tone={j.state === 'made' && j.differences.length ? 'cedar' : TONES[j.state]}>{jobWord(j)}</Tag>
                {j.seconds !== null ? <span className="num meta">{j.seconds} s</span> : null}
              </div>
              {j.failed ? <p className="mt-1 max-w-measure break-words text-sm text-alert">{failedWords(j.failed)}</p> : null}
              {j.differences.length ? (
                <div className="mt-1 text-sm text-ink-2">
                  <p>{differencesWords(j)}</p>
                  <ul className="mt-1 space-y-0.5">
                    {j.differences.map((d) => (
                      <li key={d} className="num break-words text-[0.8125rem]">
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
